import { 
    action, 
    computed, 
    observable
} from "mobx";
import get from "lodash/get";

export class ConfigStore {
    @observable configFile = <any> {};
    @observable currentModuleId = undefined; // Module id set by params or tab selection
    @observable currentFundType = undefined; // Fund type set by params or tab selection
    fundlist;

    constructor(configFile, currentModuleId, currentFundType, fundlist) {
        this.configFile = configFile;
        this.currentModuleId = currentModuleId;
        this.currentFundType = currentFundType;
        this.fundlist = fundlist;
    }

    // TOP-LEVEL FUND TYPE
    // ===================

    // Sets the current module
    @action
    setFundType = (fundType) => {
        this.currentFundType = fundType.fundType;
    }

    // Get showFundTypes
    @computed
    get showFundTypes () {
        return this.configFile.showFundTypes || false;
    }

    // Get fund types
    @computed
    get fundTypes () {
        const { fundTypes = [] } = this.configFile;

        return fundTypes;
    }

    // Get fundTypeProperty
    @computed
    get fundTypeProperty () {
        const { fundTypeProperty } = this.configFile;

        return fundTypeProperty;
    }

    // Returns selectedFundType for either the currentFundType, the first fundType set as selected, or the first fundType
    @computed
    get selectedFundType () {
        const fundType = this.fundTypes.find(
            f => this.currentFundType ? f.fundType === this.currentFundType : f.selected
        ) 
        || this.fundTypes[0] 
        || {};

        return fundType;
    }

    // MODULES FOR SELECTED FUND TYPE
    // ==============================

    // Sets the current module
    @action
    setModule = (module) => {
        this.currentModuleId = module.id;
    }
    
    // Get showFundTypes
    @computed
    get showModules () {
        return this.selectedFundType.showModules || false;
    }

    // Returns available modules from selectedFundType
    @computed
    get modules () {
        const { modules = [] } = this.selectedFundType;
        return modules;
    }

    // Returns selectedModule for either the currentModuleId, the first module set as selected, or the first module
    @computed
    get selectedModule () {
        const module = this.modules.find(
            m => this.currentModuleId ? m.id === this.currentModuleId : m.selected
        ) 
        || this.modules[0]
        || {};

        return module;
    }

    // CONFIG FOR SELECTED MODULE
    // ==========================

    // Returns config for the selected module
    @computed
    get config () {
        const { config = {} } = this.selectedModule;

        return config;
    }

    // VALUES DERIVED FROM CONFIG FOR SELECTED MODULE
    // ==============================================

    @computed
    get stickyOffset() {
        return this.config.stickyOffset || 0;
    }

    @computed
    get defaultCulture() {
        return this.config.defaultCulture || 'en-GB';
    }

    @computed
    get limit() {
        return this.config.searchLimit || 30;
    }

    @computed
    get sort() {
        return this.config.sort || [];
    }

    @computed
    get advancedSort() {
        return this.config.advancedSort;
    }

    @computed
    get watchListProperty() {
        return this.config.watchListProperty;
    }

    @computed
    get watchListEnabled() {
        return this.config.watchListEnabled;
    }

    @computed
    get fundGroupProperty() {
        return this.config.fundGroupProperty;
    }

    @computed
    get fundCurrencyProperty() {
        return this.config.fundCurrencyProperty;
    }

    @computed
    get identifierSearch() {
        return this.config.identifierSearch || {};
    }

    @computed
    get documentsJoinSpec() {
        return this.config.documentsJoinSpec || [];
    }

    @computed
    get timeseriesInclude() {
        return this.config.timeseriesInclude || {};
    }

    @computed
    get allocationsInclude() {
        return this.config.allocationsInclude || {};
    }

    @computed
    get statisticsInclude() {
        return this.config.statisticsInclude || {};
    }

    @computed
    get filtersConfig() {
        return this.config.filters || [];
    }

    @computed
    get filtersTop() {
        return this.config.filtersTop ? true : false;
    }

    @computed
    get filtersTopExpanded() {
        return this.config.hasOwnProperty('filtersTopExpanded') ? this.config.filtersTopExpanded : true;
    }

    @computed
    get filtersTopExpandable() {
        return this.config.hasOwnProperty('filtersTopExpandable') ? this.config.filtersTopExpandable : true;
    }

    @computed
    get documentSearch() {
        return this.config.documentSearch || [];
    }

    @computed
    get documentSearchBySegmentation() {
        return get(this.config.documentSearchBySegmentation, this.fundlist, []);
    }
    
    @computed
    get documentGroupMeta() {
        return this.config.documentGroupMeta || "";
    }
    
    @computed
    get documentGroupOrder() {
        return this.config.documentGroupOrder || [];
    }
    
    @computed
    get noDataPlaceholder() {
        return this.config.noDataPlaceholder || "–";
    }

    @computed
    get cultureAliases() {
        return this.config.cultureAliases || {};
    }

    @computed
    get columnConfig() {
        return this.config.columnConfig || [];
    }

    @computed
    get headerDisclaimerTypes() {
        return this.config.headerDisclaimerTypes || [];
    }

    // If present, config.footerDisclaimerTypes takes precedence over config.disclaimerTypes 
    // config.disclaimerTypes should be considered a legacy option
    @computed
    get footerDisclaimerTypes() {
        return this.config.footerDisclaimerTypes || this.config.disclaimerTypes || [];
    }

    @computed
    get rowConfig() {
        return this.config.rowConfig || {};
    }
}


// WEBPACK FOOTER //
// ./src/stores/ConfigStore.ts